// Layered ("Sugiyama-style") auto-layout for workflow graphs.
//
// The generator returns nodes at overlapping positions with oversized boxes, so a generated
// graph needs tidying: assign layers from the edge direction, order each layer to reduce edge
// crossings, then space the nodes by their real size so nothing overlaps.

const idOf = (value) => String(value ?? "");

/**
 * Order nodes into layers using the longest path from the sources. Cycles are tolerated: back
 * edges (found with a DFS) are ignored while layering, so the result is always finite.
 */
const assignLayers = (ids, forward, incoming) => {
  const layer = new Map();
  const visiting = new Set();

  const layerOf = (id) => {
    if (layer.has(id)) return layer.get(id);
    if (visiting.has(id)) return 0; // cycle guard: don't recurse forever

    visiting.add(id);
    const parents = incoming.get(id) ?? [];
    const value = parents.length === 0 ? 0 : Math.max(...parents.map((parent) => layerOf(parent) + 1));
    visiting.delete(id);
    layer.set(id, value);
    return value;
  };

  ids.forEach((id) => layerOf(id));
  return layer;
};

/**
 * Lay a workflow graph out.
 *
 * @param {object[]} nodes React Flow nodes.
 * @param {object[]} edges React Flow edges.
 * @param {object} [options]
 * @param {(node: object) => {width: number, height: number}} [options.sizeFor] Rendered size of a
 *   node (used for spacing; defaults to the node's own width/height).
 * @param {number} [options.gapX] Horizontal gap between layers.
 * @param {number} [options.gapY] Vertical gap between nodes in a layer.
 * @param {number} [options.originX]
 * @param {number} [options.originY]
 * @returns {{nodes: object[], width: number, height: number, layers: number}}
 */
export const layoutWorkflowGraph = (
  nodes = [],
  edges = [],
  {
    sizeFor = (node) => ({
      width: Number(node.width) || 320,
      height: Number(node.height) || 160,
    }),
    gapX = 140,
    gapY = 70,
    originX = 0,
    originY = 0,
  } = {}
) => {
  const list = (nodes ?? []).filter((node) => node && node.id != null);
  if (list.length === 0) return { nodes: nodes ?? [], width: 0, height: 0, layers: 0 };

  const ids = list.map((node) => idOf(node.id));
  const idSet = new Set(ids);

  const outgoing = new Map(ids.map((id) => [id, []]));
  const incoming = new Map(ids.map((id) => [id, []]));

  const validEdges = (edges ?? []).filter((edge) => {
    if (!edge) return false;
    const source = idOf(edge.source);
    const target = idOf(edge.target);
    return idSet.has(source) && idSet.has(target) && source !== target;
  });

  validEdges.forEach((edge) => {
    const source = idOf(edge.source);
    const target = idOf(edge.target);
    outgoing.get(source).push(target);
    incoming.get(target).push(source);
  });

  // Back edges (cycles) are excluded from layering and from the barycenter passes.
  const backEdges = new Set();
  const visitState = new Map();

  const visit = (id) => {
    visitState.set(id, 1);
    (outgoing.get(id) ?? []).forEach((next) => {
      const state = visitState.get(next) ?? 0;
      if (state === 1) backEdges.add(`${id}->${next}`);
      else if (state === 0) visit(next);
    });
    visitState.set(id, 2);
  };

  ids.forEach((id) => {
    if ((visitState.get(id) ?? 0) === 0) visit(id);
  });

  const forward = (id) => (outgoing.get(id) ?? []).filter((target) => !backEdges.has(`${id}->${target}`));
  const parents = (id) => (incoming.get(id) ?? []).filter((source) => !backEdges.has(`${source}->${id}`));

  const layerOf = assignLayers(ids, forward, new Map(ids.map((id) => [id, parents(id)])));

  const layers = [];
  list.forEach((node) => {
    const id = idOf(node.id);
    const index = layerOf.get(id) ?? 0;
    layers[index] = layers[index] ?? [];
    layers[index].push(node);
  });

  // Fill any gaps so layer indexes are contiguous.
  for (let index = 0; index < layers.length; index += 1) {
    layers[index] = layers[index] ?? [];
  }

  // Crossing reduction: order each layer by the barycenter of its neighbours, sweeping back and
  // forth a few times.
  const orderOf = new Map();
  const reindex = () => {
    layers.forEach((group, layerIndex) => {
      group.forEach((node, index) => orderOf.set(idOf(node.id), { layer: layerIndex, index }));
    });
  };
  reindex();

  const sortLayer = (layerIndex, neighbourIds) => {
    const group = layers[layerIndex];
    if (!group) return;

    const barycenter = (node) => {
      const neighbours = neighbourIds(node).map((id) => orderOf.get(id)).filter(Boolean);
      if (neighbours.length === 0) return Number.MAX_SAFE_INTEGER;
      return neighbours.reduce((sum, entry) => sum + entry.index, 0) / neighbours.length;
    };

    group.sort((a, b) => barycenter(a) - barycenter(b));
    group.forEach((node, index) => orderOf.set(idOf(node.id), { layer: layerIndex, index }));
  };

  for (let pass = 0; pass < 4; pass += 1) {
    for (let layerIndex = 1; layerIndex < layers.length; layerIndex += 1) {
      sortLayer(layerIndex, (node) => parents(idOf(node.id)));
    }
    for (let layerIndex = layers.length - 2; layerIndex >= 0; layerIndex -= 1) {
      sortLayer(layerIndex, (node) => forward(idOf(node.id)));
    }
  }

  // Sizes are supplied by the caller (the dynamic renderer knows each node's real box).
  const sizes = new Map();
  list.forEach((node) => {
    const size = sizeFor(node) ?? {};
    sizes.set(idOf(node.id), {
      width: Math.max(80, Number(size.width) || 320),
      height: Math.max(40, Number(size.height) || 160),
    });
  });

  const layerWidth = layers.map((group) =>
    group.reduce((max, node) => Math.max(max, sizes.get(idOf(node.id)).width), 0)
  );
  const layerHeight = layers.map((group) =>
    group.reduce((sum, node) => sum + sizes.get(idOf(node.id)).height, 0) +
    Math.max(0, group.length - 1) * gapY
  );

  const tallest = layerHeight.length ? Math.max(...layerHeight) : 0;

  let cursorX = originX;
  const layerX = layers.map((_, layerIndex) => {
    const x = cursorX;
    cursorX += layerWidth[layerIndex] + gapX;
    return x;
  });

  const positions = new Map();
  layers.forEach((group, layerIndex) => {
    let cursorY = originY + (tallest - layerHeight[layerIndex]) / 2;

    group.forEach((node) => {
      const size = sizes.get(idOf(node.id));
      positions.set(idOf(node.id), { x: Math.round(layerX[layerIndex]), y: Math.round(cursorY) });
      cursorY += size.height + gapY;
    });
  });

  const nextNodes = (nodes ?? []).map((node) => {
    const position = positions.get(idOf(node?.id));
    return position ? { ...node, position } : node;
  });

  return {
    nodes: nextNodes,
    width: Math.max(0, cursorX - gapX - originX),
    height: tallest,
    layers: layers.length,
  };
};

export default layoutWorkflowGraph;
